import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";
import { Subject, combineLatest } from "rxjs";
import { map, takeUntil, tap } from "rxjs/operators";
import { Configuration } from "src/app/shared/configuration.enum";
import { CoreService } from "src/app/shared/services/core.service";
import { SignupService } from "src/app/signup/service/signup.service";
import {
  DEFAULT_TITLE_TERMS_AND_DISCLOSURES,
  ERROR_PATH,
  PRODUCT_TISA_URL_SETTING_KEY,
  TERM_CONDITIONS_FRAME_HEIGHT_DESKTOP,
  TERM_CONDITIONS_FRAME_HEIGHT_LOW_RESOLUTION,
  TERM_CONDITIONS_FRAME_HEIGHT_MOBILE,
} from "../../shared/constants";
import { DeviceType } from "../../shared/model/device-type";
import { FacadeService } from "../../shared/service-facade/facade.service";
import { TermsAndDisclosuresService } from "./terms-disclosures.service";
import { EligibilityService } from "src/app/shared/services/eligibility.service";
import { ReferenceCode } from "src/app/process/models/referenceCode";
import { ApplicationDetailLog,ApplicationLogsRequest} from "leia-api";

import StatusEnum = ApplicationLogsRequest.StatusEnum;


function noFalseValues(array: any[]): boolean {
  return !array.includes(false);
}
@Component({
  selector: "app-view-disclosures",
  templateUrl: "./view-disclosures.component.html",
  styleUrls: ["./view-disclosures.component.scss"],
})
export class ViewDisclosuresComponent implements OnInit, OnDestroy {
  public step = 0;
  public termsAndDisclosuresTitle: string;
  public frameHeight: number = TERM_CONDITIONS_FRAME_HEIGHT_DESKTOP;

  bankingTermsDisclosuresUrl = "";
  electronicBankingTermsDisclosuresUrl = "";
  tisaUrl = "";
  privacyNoticeUrl = "";
  privacyPolicyUrl = "";
  esignUrl = "";
  termsDisclosuresUrl = "";
  brandName = "";
  customerSupportPhone = "";
  privacySecurityCenterUrl = "";
  accountTISAs: TISADownload[] = [];

  termsDisclosuresForm = new FormGroup({
    isAccepted: new FormControl(false),
  });

  termsDisclosuresComplete$ = this.termsDisclosuresForm
    .get("isAccepted")
    ?.valueChanges.pipe(
      tap((value) => {
        if (!value) {
          this.generalDisclosuresAccepted$.next(false);
        }
      })
    );
  generalDisclosuresAccepted$: Subject<boolean> = new Subject();
  eSignAccepted$: Subject<boolean> = new Subject();
  taxCertificationAccepted$: Subject<boolean> = new Subject();

  destroySubject$ = new Subject<void>();

  generalDisclosuresCompleted$ = combineLatest([
    this.termsDisclosuresComplete$,
    this.generalDisclosuresAccepted$,
    this._termsAndDisclosuresService.canContinueGeneralDisclosures$,
  ]).pipe(map(noFalseValues));

  allDocumentsAccepted$ = combineLatest([
    this.generalDisclosuresCompleted$,
    this.eSignAccepted$,
    this.taxCertificationAccepted$,
  ]).pipe(map(noFalseValues));

  get isMobile(): boolean {
    return this.facadeService.getDeviceType() === DeviceType.MOBILE;
  }

  constructor(
    private facadeService: FacadeService,
    private coreService: CoreService,
    private _termsAndDisclosuresService: TermsAndDisclosuresService,
    private _signupService: SignupService,
    private eligibilityService: EligibilityService
  ) {
    this.termsAndDisclosuresTitle = DEFAULT_TITLE_TERMS_AND_DISCLOSURES;

    let termsAndDisclosuresTitleTemp: string =
      this.facadeService.getStorageItem("termsAndDisclosuresTitle");
    if (termsAndDisclosuresTitleTemp != null)
      this.termsAndDisclosuresTitle = termsAndDisclosuresTitleTemp;

    if (
      this.facadeService.getDeviceType() === DeviceType.MOBILE ||
      this.facadeService.getDeviceType() === DeviceType.TABLET
    )
      this.frameHeight = TERM_CONDITIONS_FRAME_HEIGHT_MOBILE;
    if (window.innerHeight < 600)
      this.frameHeight = TERM_CONDITIONS_FRAME_HEIGHT_LOW_RESOLUTION;
  }

  private _config(key: Configuration): string {
    return this.facadeService.getConfigurationSetting(key);
  }

  ngOnInit(): void {
    this.checkEligibility();   

    this.bankingTermsDisclosuresUrl = this._config(
      Configuration.BANKING_TERMS_DISCLOSURES_URL
    );
    this.electronicBankingTermsDisclosuresUrl = this._config(
      Configuration.ELECTRONIC_TERMS_DISCLOSURES_URL
    );
    this.tisaUrl = this._config(Configuration.TISA_URL);
    this.privacyNoticeUrl = this._config(Configuration.PRIVACY_NOTICE_URL);
    this.privacyPolicyUrl = this._config(Configuration.PRIVACY_POLICY_URL);
    this.esignUrl = this._config(Configuration.ESIGN_URL);
    this.termsDisclosuresUrl = this._config(
      Configuration.TERMS_DISCLOSURES_URL
    );
    this.brandName = this.coreService.brandNameSubject.value;
    this.customerSupportPhone = this._config(
      Configuration.CUSTOMER_SUPPORT_PHONE
    );
    this.privacySecurityCenterUrl = this._config(
      Configuration.PRIVACY_SECURITY_CENTER_URL
    );

    this._getAccountTISAs();

    this._termsAndDisclosuresService.updateTisaUrl$
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(() => {
        this.tisaUrl = this._config(Configuration.TISA_URL);
      });
    this.allDocumentsAccepted$
      .pipe(takeUntil(this.destroySubject$))
      .subscribe((value) => {
        this._termsAndDisclosuresService.setTermsDisclosuresCompleted(value);
      });
  }

  ngOnDestroy(): void {
    this.destroySubject$.next();
  }
  
  private async checkEligibility() {
    const canProceed = await this.executeFullEligibilityCheck();
    this.eligibilityService.setCanProceed(canProceed);
  }

  async executeFullEligibilityCheck(): Promise<boolean> {
    const customerEligibility: any =
      await this.eligibilityService.checkCustomerEligibility();
    await this._refreshApplicationLogProductState();

    //if eligibility data is missing or empty (applicants [] / products [])
    if (
      Object.keys(customerEligibility).length === 0 ||
      (!customerEligibility.rcCode &&
        (!customerEligibility.applicants?.length ||
          !customerEligibility.products?.length))
    ) {
      return this.handleEligibilityError(ReferenceCode.InternalFailure);
    }

    //if ineligibile based on rcCode
    if (customerEligibility && customerEligibility.rcCode) {
      // ineligible
      return this.handleEligibilityError(customerEligibility.rcCode);
    }
    return true;
  }

  private async _refreshApplicationLogProductState(): Promise<void> {
    const applicationLogResponse: ApplicationDetailLog =
      await this.facadeService
        .getApplicationLog(this.facadeService.getSessionId())
        .toPromise();
    if (applicationLogResponse) {
      let applicationRequest =
        this.facadeService.getShoppingCartApplicationLogsRequest();
      applicationRequest.products = applicationLogResponse.products;
      applicationRequest.status = applicationLogResponse.status! as StatusEnum;
      this.facadeService.setShoppingCartApplicationLogsRequest(
        applicationRequest
      );
    }
  }

  handleEligibilityError(rcCode: string) {
    this.facadeService
      .processOnboardingError(rcCode as ReferenceCode)
      .subscribe(() => {
        this.coreService.navigate(`/${ERROR_PATH}`);
      });
    return false;
  }

  private _getAccountTISAs(): void {
    const formValue = this._signupService.accountSelection.value;
    formValue.accounts.forEach((selectedAccount: any) => {
      const { account } = selectedAccount;
      const accountConfig = this.facadeService.getAccountConfiguration(account);
      const { name } = accountConfig;

      const settings = this.facadeService.productSettings.get(account);
      const TISA = settings?.get(PRODUCT_TISA_URL_SETTING_KEY) as string;
      if (TISA) {
        this.accountTISAs.push({
          accountName: name!,
          PDFUrl: TISA!,
        });
      }
    });
  }

  acceptDocument(index: number): void {
    if (index === 0) {
      this.eSignAccepted$.next(true);
    }
    if (index === 1) {
      this.generalDisclosuresAccepted$.next(true);
    }
    if (index === 2) {
      this.taxCertificationAccepted$.next(true);
    }
  }

  setStep(index: number) {
    this.step = index;
  }

  nextStep() {
    this.step++;
  }

  downloadDocument(link: string): void {
    window.open(link);
  }
}

export interface TISADownload {
  accountName: string;
  PDFUrl: string;
}
